// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { interviewAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Clock, ChevronLeft, ChevronRight, CheckCircle2, Eye, EyeOff,
  Save, Square, Play, Mic, MessageSquare, SkipForward, Zap, Target
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = { asked: "Posée", partially_covered: "Partielle", deep_dive: "Approfondie", skipped: "Non posée" };
const STATUS_ICONS = { asked: "✅", partially_covered: "🔶", deep_dive: "🔷", skipped: "⬜" };
const OBS_LABELS = { communication: "Communication", clarity: "Clarté", rigor: "Rigueur", autonomy: "Autonomie", depth: "Profondeur technique" };

export default function InterviewLivePage() {
  const { workspaceId, interviewId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [interview, setInterview] = useState(null);
  const [questions, setQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [timer, setTimer] = useState(0);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const interviewRef = useRef(null);

  useEffect(() => {
    interviewRef.current = interview;
  }, [interview]);

  useEffect(() => { loadData(); return () => { clearInterval(timerRef.current); clearInterval(autoSaveRef.current); }; }, [interviewId]);

  const loadData = async () => {
    try {
      const itw = await interviewAPI.getInterview(interviewId);
      setInterview(itw.data);

      // Fetch all question texts
      const allQIds = itw.data.sections.flatMap(s => s.responses.map(r => r.question_id));
      const qRes = await interviewAPI.listQuestions(workspaceId, { limit: 500 });
      const qMap = {};
      qRes.data.forEach(q => { qMap[q.id] = q; });
      setQuestions(qMap);

      // Start timer if not already started
      if (itw.data.status === "draft") {
        await interviewAPI.updateInterview(interviewId, { status: "in_progress" });
        setInterview(prev => ({ ...prev, status: "in_progress" }));
      }

      // Calculate elapsed time
      if (itw.data.started_at) {
        const started = new Date(itw.data.started_at);
        setTimer(Math.floor((Date.now() - started.getTime()) / 1000));
      }

      // Start timer
      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);

      // Autosave every 15 seconds
      autoSaveRef.current = setInterval(() => {
        const current = interviewRef.current;
        if (current) {
          interviewAPI.updateInterview(interviewId, {
            sections: current.sections,
            observations: current.observations,
            global_notes: current.global_notes,
          }).catch(() => {});
        }
      }, 15000);

    } catch (e) { toast.error("Erreur"); navigate(-1); } finally { setLoading(false); }
  };

  const formatTimer = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h > 0 ? h + ":" : ""}${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const updateResponse = (sIdx, qIdx, field, value) => {
    const sections = [...interview.sections];
    sections[sIdx].responses[qIdx] = { ...sections[sIdx].responses[qIdx], [field]: value };
    setInterview({ ...interview, sections });
  };

  const updateObservation = (key, value) => {
    setInterview({ ...interview, observations: { ...interview.observations, [key]: value } });
  };

  const handleSaveManual = async () => {
    setSaving(true);
    try {
      await interviewAPI.updateInterview(interviewId, {
        sections: interview.sections,
        observations: interview.observations,
        global_notes: interview.global_notes,
      });
      toast.success("Sauvegardé");
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const handleComplete = async () => {
    try {
      // Final save
      await interviewAPI.updateInterview(interviewId, {
        sections: interview.sections,
        observations: interview.observations,
        global_notes: interview.global_notes,
      });
      // Complete
      await interviewAPI.completeInterview(interviewId);
      clearInterval(timerRef.current);
      clearInterval(autoSaveRef.current);
      toast.success("Entretien terminé !");
      navigate(`/workspace/${workspaceId}/interviews/summary/${interviewId}`);
    } catch (e) { toast.error("Erreur"); }
  };

  const goToQuestion = (sIdx, qIdx) => {
    setCurrentSection(sIdx);
    setCurrentQuestion(qIdx);
    setShowAnswer(false);
  };

  const nextQuestion = () => {
    const section = interview.sections[currentSection];
    if (currentQuestion < section.responses.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < interview.sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentQuestion(0);
    }
    setShowAnswer(false);
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      const prevSec = interview.sections[currentSection - 1];
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(prevSec.responses.length - 1);
    }
    setShowAnswer(false);
  };

  if (loading || !interview) {
    return <div className="flex items-center justify-center h-screen bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const section = interview.sections[currentSection];
  const response = section?.responses[currentQuestion];
  const question = response ? questions[response.question_id] : null;

  const totalAnswered = interview.sections.reduce((acc, s) => acc + s.responses.filter(r => r.status !== "skipped").length, 0);
  const totalQuestions = interview.sections.reduce((acc, s) => acc + s.responses.length, 0);

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar */}
      <header className="h-14 border-b flex items-center px-4 gap-4 bg-card shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/interviews/candidates/${interview.candidate_id}`)}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Mic className="h-5 w-5 text-primary" />
          <span className="font-semibold">{interview.candidate_name}</span>
          <span className="text-muted-foreground">—</span>
          <span className="text-sm text-muted-foreground">{interview.template_title}</span>
        </div>
        <div className="flex-1" />
        <Badge variant="outline" className="font-mono text-base gap-1">
          <Clock className="h-4 w-4" />{formatTimer(timer)}
        </Badge>
        <Badge variant="secondary">{totalAnswered}/{totalQuestions}</Badge>
        <Button variant="outline" size="sm" onClick={handleSaveManual} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        </Button>
        <Button size="sm" onClick={() => setShowComplete(true)} className="gap-1 bg-emerald-600 hover:bg-emerald-700">
          <Square className="h-3 w-3" />Terminer
        </Button>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar — Sections */}
        <aside className="w-64 border-r bg-card overflow-y-auto shrink-0 hidden md:block">
          <div className="p-3 space-y-1">
            {interview.sections.map((s, sIdx) => {
              const answered = s.responses.filter(r => r.status !== "skipped").length;
              const total = s.responses.length;
              const isActive = sIdx === currentSection;
              return (
                <div key={s.section_id || sIdx}>
                  <button
                    className={`w-full text-left p-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"}`}
                    onClick={() => { setCurrentSection(sIdx); setCurrentQuestion(0); setShowAnswer(false); }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{s.title}</span>
                      <span className="text-xs text-muted-foreground">{answered}/{total}</span>
                    </div>
                    <div className="mt-1 bg-muted rounded-full h-1.5">
                      <div className="bg-primary rounded-full h-1.5 transition-all" style={{ width: `${total > 0 ? (answered / total) * 100 : 0}%` }} />
                    </div>
                  </button>
                  {isActive && (
                    <div className="ml-3 pl-2 border-l space-y-0.5 mt-1 mb-2">
                      {s.responses.map((r, qIdx) => (
                        <button key={r.question_id} onClick={() => goToQuestion(sIdx, qIdx)}
                          className={`w-full text-left px-2 py-1 rounded text-xs transition-colors flex items-center gap-1.5 ${qIdx === currentQuestion ? "bg-primary/5 font-medium" : "hover:bg-muted/50"}`}>
                          <span>{STATUS_ICONS[r.status]}</span>
                          <span className="truncate">{questions[r.question_id]?.question_text?.substring(0, 40) || `Q${qIdx + 1}`}...</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main Content — Current Question */}
        <main className="flex-1 overflow-y-auto p-6">
          {question ? (
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Section indicator */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{section.title}</span>
                <span>·</span>
                <span>Question {currentQuestion + 1} / {section.responses.length}</span>
                {question.domain && <><span>·</span><Badge variant="outline" className="text-xs">{question.domain}</Badge></>}
                {question.difficulty && <Badge variant="secondary" className="text-xs">{question.difficulty}</Badge>}
              </div>

              {/* Question */}
              <Card>
                <CardContent className="pt-6">
                  <p className="text-lg font-medium leading-relaxed">{question.question_text}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{question.max_score} pts max</span>
                    <span>·</span>
                    <span>~{question.estimated_minutes} min</span>
                    <span>·</span>
                    <span>Coef. {question.weight}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Expected Answer (toggle) */}
              <Button variant="outline" className="gap-2 w-full justify-start" onClick={() => setShowAnswer(!showAnswer)}>
                {showAnswer ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showAnswer ? "Masquer la réponse attendue" : "Voir la réponse attendue"}
              </Button>
              {showAnswer && question.expected_answer && (
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="pt-4">
                    <p className="text-sm whitespace-pre-wrap">{question.expected_answer}</p>
                    {question.key_points?.length > 0 && (
                      <ul className="mt-2 list-disc list-inside text-sm space-y-0.5">
                        {question.key_points.map((kp, i) => <li key={i} className="text-muted-foreground">{kp}</li>)}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Scoring */}
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Score</span>
                    <span className="text-2xl font-bold text-primary">{response.score} / {response.max_score}</span>
                  </div>
                  <Slider
                    value={[response.score]}
                    onValueChange={(v) => updateResponse(currentSection, currentQuestion, "score", v[0])}
                    max={response.max_score}
                    step={0.5}
                    className="mt-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0</span><span>{response.max_score / 2}</span><span>{response.max_score}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Status */}
              <div className="flex gap-2">
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <Button key={k} variant={response.status === k ? "default" : "outline"} size="sm"
                    onClick={() => updateResponse(currentSection, currentQuestion, "status", k)}
                    className="gap-1">
                    <span>{STATUS_ICONS[k]}</span>{v}
                  </Button>
                ))}
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium mb-1 block">Notes / Commentaires</label>
                <Textarea
                  value={response.notes}
                  onChange={(e) => updateResponse(currentSection, currentQuestion, "notes", e.target.value)}
                  placeholder="Observations sur la réponse du candidat..."
                  rows={4}
                />
              </div>

              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={prevQuestion} disabled={currentSection === 0 && currentQuestion === 0}>
                  <ChevronLeft className="h-4 w-4 mr-1" />Précédente
                </Button>
                <Button onClick={nextQuestion}
                  disabled={currentSection === interview.sections.length - 1 && currentQuestion === section.responses.length - 1}>
                  Suivante<ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Sélectionnez une question
            </div>
          )}
        </main>

        {/* Right Sidebar — Observations */}
        <aside className="w-72 border-l bg-card overflow-y-auto shrink-0 hidden lg:block p-4 space-y-4">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Target className="h-4 w-4" />Observations</h3>
          {Object.entries(interview.observations || {}).map(([key, score]) => (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm">{OBS_LABELS[key] || key}</span>
                <span className="text-sm font-bold text-primary">{score}/5</span>
              </div>
              <Slider value={[score]} onValueChange={(v) => updateObservation(key, v[0])} max={5} step={1} />
            </div>
          ))}

          <Separator className="my-4" />

          <h3 className="font-semibold text-sm flex items-center gap-2"><MessageSquare className="h-4 w-4" />Notes globales</h3>
          <Textarea
            value={interview.global_notes || ""}
            onChange={(e) => setInterview({ ...interview, global_notes: e.target.value })}
            placeholder="Impression générale, points marquants..."
            rows={6}
            className="text-sm"
          />
        </aside>
      </div>

      {/* Complete Dialog */}
      <AlertDialog open={showComplete} onOpenChange={setShowComplete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Terminer l'entretien ?</AlertDialogTitle>
            <AlertDialogDescription>
              {totalAnswered}/{totalQuestions} questions évaluées. Les scores seront calculés automatiquement et une recommandation sera générée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuer l'entretien</AlertDialogCancel>
            <AlertDialogAction onClick={handleComplete} className="bg-emerald-600 hover:bg-emerald-700">Terminer et voir la synthèse</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
